import get from "lodash/get";
import set from "lodash/set";
import has from "lodash/has";
import omit from "lodash/omit";
import size from "lodash/size";
import pick from "lodash/pick";
import keyBy from "lodash/keyBy";
import isNil from "lodash/isNil";
import isEmpty from "lodash/isEmpty";
import omitBy from "lodash/omitBy";
import mapValues from "lodash/mapValues";
import { compareKeys, modify } from "mythil/object";
import { getModelTitle, getColumns, } from "~/stateGetter";
import { StateGetterBase } from "./StateGetterBase";
import { getUniqueKey } from "~/lib/uniqueId";
import { presentFilters, presentAppliedFilterValuesObject } from "~/statePresenter";
import { isDateFilter } from "~/lib/filterTypes";
import { getAllResultStateInfos, getCalendarViews, getPredefinedViews, getResultInfo } from "../stateGetter";

const EMPTY_OBJECT = {}
export class FilteredViewState extends StateGetterBase {
    get activeFilteredViewName() {
        return get(this.getModel(), `activeView.viewKey`) ?? get(this.getModel(), `settings.activeFilteredView`);
    }

    get activeViewSort() {
        return get(this.getWorkingView(), `sortOptions`, EMPTY_OBJECT);
    }

    get activeViewFilterOrder() {
        return get(this.getModel(), `settings.filterOrder`, EMPTY_OBJECT);
    }

    get activeViewColumnVisibility() {
        return get(this.getWorkingView(), `columnVisibility`, EMPTY_OBJECT);
    }

    get activeViewCalendarViewConfig() {
        return get(this.getWorkingView(), `calendarViewConfig`, EMPTY_OBJECT);
    }

    get count() {
        return size(this.getFilteredViews());
    }

    get countAll() {
        return size({ ...this.getFilteredViews(), ...this.getSharedViews() });
    }

    get hasChanged() {
        const calendarFilter = this.getCalendarConfig().enabled && get(getCalendarViews(this.state, this.modelId), "0.filter_name");
        const nestedCompare = (prev, current, key) => {
            if (prev === current) { return true; }
            if (key === "createdAt" && (isNil(prev) || isNil(current))) { return true; }
            if (key === "created_at" && typeof prev !== "object" && typeof current !== "object") { return true; }

            if (key === "filters") {
                prev = this.getCleanedWorkingFilters(prev ?? null);
                current = this.getCleanedWorkingFilters(current);
                if (calendarFilter && key === "filters") {
                    return nestedCompare(omit(prev, calendarFilter), omit(current, calendarFilter))
                }
            }

            if (typeof prev !== typeof current) { return false; }
            if (typeof prev !== "object") { return prev === current; }

            return compareKeys(prev, current, nestedCompare);
        }

        const existingModelSettings = get(this.state.ui.settings, `${this.tableSettingsIdentifier}`, {});

        if (!existingModelSettings.filteredViews) {
            return this.getWorkingView().hasChanged;
        }

        const { filteredViews, filterOrder, activeFilteredView } = get(this.getMergedSettings(), this.tableSettingsIdentifier, {});
        return !nestedCompare(
            {
                view: get(existingModelSettings, `filteredViews.${this.activeFilteredViewName}`),
                filterOrder: existingModelSettings.filterOrder,
                activeFilteredView: existingModelSettings.activeFilteredView
            },
            {
                view: get(filteredViews, this.activeFilteredViewName),
                activeFilteredView,
                filterOrder,
            },
        )
    }

    isSharedView(view = this.activeFilteredViewName) {
        return !has(this.getFilteredViews(), view);
    }

    getFilteredViews() {
        return get(this.getModel(), `settings.filteredViews`, EMPTY_OBJECT);
    }
    
    getSharedViews() {
        return get(this.state, `ui.sharedViews.${this.tableSettingsIdentifier}`, EMPTY_OBJECT);
    }
    
    getDefaultViews() {
        return getPredefinedViews(this.state, this.modelId);
    }
    
    getCalendarConfig() {
        return this.activeViewCalendarViewConfig;
    }
    
    getColumnVisibility() {
        return this.activeViewColumnVisibility;
    }

    getSortOrder() {
        return this.activeViewSort;
    }

    getFilterOrder() {
        return this.activeViewFilterOrder;
    }

    getViewOrDefault (viewName, defaultView) {
        return get(this.getFilteredViews(), viewName) || get(this.getSharedViews(), viewName) || get(this.getDefaultViews(), viewName) || defaultView;
    }

    getView (viewName, defaultValue = EMPTY_OBJECT) {
        return this.getViewOrDefault(viewName, defaultValue);
    }

    getActiveView() {
        return this.getViewOrDefault(this.activeFilteredViewName);
    }

    getWorkingView() {
        return this.getModel().activeView
    }

    cleanView(view) {
        view = omit(view, "previousTitle", "hasChanged");

        view = modify(view, "columnVisibility", (visibility) => {
            if (!visibility || isEmpty(visibility)) return undefined;
            const modelColumns = getColumns(this.state, this.modelId);
            return pick(visibility, Object.keys(modelColumns));
        });

        view = modify(view, "columnWidths", (columnWidths) => {
            if (!columnWidths || isEmpty(columnWidths)) return undefined;
            const modelColumns = getColumns(this.state, this.modelId);
            return pick(columnWidths, Object.keys(modelColumns));
        });


        view = modify(view, 'createdAt', view.createdAt || new Date().getTime());
        view = modify(view, 'title', view.title ?? getModelTitle(this.state, this.modelId));

        return view;
    }

    getCleanWorkingView(config) {
        let view = omit(this.getWorkingView(), "sort", "previousTitle", "shouldSetPath", "key", "hasChanged");

        if (!view.filters) {
            return view;
        }
        
        return modify(view, `filters`, this.getCleanedWorkingFilters());
    }

    getCleanedWorkingFilters(filters = this.getWorkingView()?.filters) {
        if (!filters) {
            return;
        }
        

        const resultInfo = getAllResultStateInfos(this.state, this.modelId);
        const filterValues = omit(
            presentAppliedFilterValuesObject({
                ...resultInfo,
                itemId: null,
                filters: keyBy(presentFilters(resultInfo), "name"),
                filterValues: omitBy(filters, (filterValue) => isNil(filterValue)),
            }),
            [...([resultInfo.parentSubview?.filterName] ?? [])]
        )

        if (isEmpty(filterValues)) return;

        return mapValues(
            presentAppliedFilterValuesObject({
                ...resultInfo,
                itemId: null,
                filterValues,
                filters: keyBy(presentFilters(resultInfo), "name"),
            }),
            (filter) => omitBy({
                ...filter,
                value: isDateFilter(filter.filterType) ? undefined : filter.value
            }, x => x === undefined)
        )
    }

    getOwnActiveView() {
        return get(this.getFilteredViews(), this.activeFilteredViewName)
    }

    getMergedSettings() {
        const activeView = this.getCleanWorkingView();
        
        const identifier = this.tableSettingsIdentifier;
        const originalSettings = get(this.state, ["ui", "settings"]);

        const prepareNextView = () => {
            const activeKey = activeView.viewKey;
            let { filteredViews, ...restModelSettings} = this.getModel().settings ?? {};

            if (!this.getOwnActiveView()) return {
                activeFilteredView: activeKey,
                filterOrder: restModelSettings.filterOrder,
            };

                
            filteredViews = modify(filteredViews, `${activeKey}`, activeView)
            filteredViews = modify(filteredViews, `${activeKey}.viewKey`, activeKey)
            filteredViews = modify(filteredViews, `${activeKey}.isShared`, activeView.isShared && activeKey === activeView.viewKey)
            filteredViews = modify(filteredViews, `${activeKey}`, (view) => omitBy(view, (x) => isNil(x)))

            return {
                ...restModelSettings,
                filteredViews,
            }
        }

        return {
            ...originalSettings,
            [identifier]: {
                ...get(originalSettings, identifier, {}),
                ...prepareNextView(),
            }
        }
    }

    getFilteredViewProps() {
        return {
            views: this.getFilteredViews(),
            sharedViews: this.getSharedViews(),
            defaultViews: this.getDefaultViews(),
            modelName: this.modelName
        }
    }

    getFilteredViewTitle() {
        const filteredViews = this.getFilteredViews(),
              activeFilteredView = this. activeFilteredViewName;

        return this.getView(activeFilteredView)?.title || filteredViews[activeFilteredView]?.previousTitle || getModelTitle(this.state, this.modelId)
    }
}


export const getFitleredViewState = (state, modelId) =>  new FilteredViewState(state, modelId)